import type { KVNamespace } from "@cloudflare/workers-types";

import mockPlatformConfiguration from "~/mockData/platformConfigurations/mws";
import type { PlatformConfiguration } from "~/types/PlatformConfiguration";
import { $fetchWithTimeout } from "~/utils/promiseWithTimeout";

export default defineNuxtPlugin({
  parallel: true,
  name: "platform",
  dependsOn: ["vuex", "whitelabelRedirects"],
  setup: async (nuxtApp) => {
    const platformStore = usePlatformStore();

    // we skip the execution of fetching the platform configuration in the test environment
    // because it is currently not possible to mock anything in a plugin (see: https://github.com/danielroe/nuxt-vitest/issues/181)
    // instead, we add the platform configuration to the store in the `vitest.setup.ts` file
    if (nuxtApp.$config.public.nodeEnv === "test") {
      platformStore.setPlatformConfiguration(mockPlatformConfiguration);

      return;
    }

    const host = discoverHost(nuxtApp.$config.public.devHost);

    // if the call to the website requires no-cache, we also want to invalidate the cache for the platform config (mostly useful for e2e tests)
    const cacheControlHeader = useRequestHeaders(["Cache-Control"])[
      "cache-control"
    ];

    const fetchBackendPlatformConfig = (host: string) => {
      return $fetchWithTimeout<PlatformConfiguration>(
        `${nuxtApp.$config.public.platformServiceUrl}/resolve`,
        {
          params: {
            domain: host,
          },
          headers: cacheControlHeader
            ? { "Cache-Control": cacheControlHeader }
            : undefined,
        },
      );
    };

    const platformConfigurationsKvStore = nuxtApp.ssrContext?.event.context
      .cloudflare?.env.PLATFORM_CONFIGURATIONS as KVNamespace;

    // using `useAsyncData` for deduplication of the request between the server and client
    const { data: resolvedPlatformConfiguration, error } = await useAsyncData(
      `PLATFORM_CONFIGURATION-${host}`,
      async () => {
        // If we run a cloudflare build, use the KV store
        if (
          platformConfigurationsKvStore &&
          !cacheControlHeader?.includes("no-cache")
        ) {
          let kvPlatformConfiguration;

          try {
            kvPlatformConfiguration =
              await platformConfigurationsKvStore.get<PlatformConfiguration>(
                host,
                "json",
              );
          } catch {
            // eslint-disable-next-line no-console
            console.warn("Failed to get platform configuration from KV store.");
          }

          // Check if we've already saved the platform config in our cache
          if (!kvPlatformConfiguration) {
            const backendPlatformConfiguration =
              await fetchBackendPlatformConfig(host);

            try {
              // If not, put the backend platform configuration to the KV store
              await platformConfigurationsKvStore.put(
                host,
                JSON.stringify(backendPlatformConfiguration),
                // https://developers.cloudflare.com/kv/api/write-key-value-pairs/#create-expiring-keys
                { expirationTtl: 60 * 30 }, // 30 minutes
              );
            } catch {
              // eslint-disable-next-line no-console
              console.warn("Failed to put platform configuration to KV store.");
            }

            return backendPlatformConfiguration;
          } else {
            return kvPlatformConfiguration;
          }
        } else {
          // If no cloudflare, just fetch from backend
          return await fetchBackendPlatformConfig(host);
        }
      },
    );

    if (error.value) {
      if (error.value.statusCode === 503) {
        platformStore.setPlatformIsInMaintenanceMode(true);
      }

      if (error.value.statusCode === 404 || error.value.statusCode === 500) {
        reloadNuxtApp();
      }

      throw createError({
        statusCode: error.value.statusCode,
        message: `Error ${error.value.statusCode}. The platform configuration for the host "${host}" could not be resolved.`,
      });
    }

    if (!resolvedPlatformConfiguration.value) {
      throw new Error(
        `The platform configuration for the host "${host}" could not be resolved.`,
      );
    }

    platformStore.setPlatformConfiguration(resolvedPlatformConfiguration.value);
  },
});
